import { useMutation } from '@apollo/client';
import {
    ButtonDock,
    miniButtonDockScrollBottomPad,
    setTemplateTranslations,
    translateTemplateText,
    useLiquidGlassPress,
} from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { client, loginData } from '../../client/apollo';
import { CreateCategoryMutation, CreateCategoryMutationVariables, FinalizeRegistrationMutation } from '../../client/generated/graphql';
import { createCategoryMutation } from '../../client/mutations/createCategory';
import { finalizeRegistrationMutation } from '../../client/mutations/finalizeRegistration';
import { ListTemplateCategory } from '../../client/types';
import { CommonIcon } from '../../components/common/CommonIcon';
import { LiquidGlassRowCard } from '../../components/common/LiquidGlassRowCard';
import { Page } from '../../components/Page';
import { AnalyticsEventType } from '../../contexts/analyticsEvents';
import { getColor } from '../../helpers/color';
import { useAnalytics } from '../../hooks/useAnalytics';

interface SelectableTemplate {
    template: ListTemplateCategory;
    selected: boolean;
}

type SelectableTemplateRowProps = {
    item: SelectableTemplate;
    onToggle: () => void;
    showDivider: boolean;
};

const SelectableTemplateRow: FC<SelectableTemplateRowProps> = ({ item, onToggle, showDivider }) => {
    const { t } = useTranslation();
    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick } = useLiquidGlassPress({
        preventDefault: true,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            onToggle();
        },
    });
    const templateName = translateTemplateText(t, item.template.id, 'name', item.template.name);
    const templateDescription = translateTemplateText(t, item.template.id, 'description', item.template.description ?? '');

    return (
        <Box
            component="button"
            type="button"
            aria-label={templateName}
            aria-pressed={item.selected}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            sx={{
                display: 'block',
                width: '100%',
                m: 0,
                px: '14px',
                py: '12px',
                border: 'none',
                borderTop: showDivider ? '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)' : 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                bgcolor: pressed
                    ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)'
                    : item.selected
                    ? 'color-mix(in srgb, var(--tg-theme-button-color) 12%, transparent)'
                    : 'transparent',
                transition: 'background-color 0.12s ease',
            }}
        >
            <Stack direction="row" alignItems="center" gap={1.5}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        backgroundColor: getColor(item.template.iconColor),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CommonIcon icon={item.template.icon} color="var(--tg-theme-text-color)" size={26} type="filled" />
                </Box>
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                    <Text weight="2" style={{ fontSize: 15, lineHeight: 1.2 }}>
                        {templateName}
                    </Text>
                    <Caption
                        style={{
                            color: 'var(--tg-theme-hint-color)',
                            fontSize: 12,
                            lineHeight: '16px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {templateDescription}
                    </Caption>
                </Stack>
                <CommonIcon
                    icon={item.selected ? 'check_circle' : 'radio_button_unchecked'}
                    type="filled"
                    size={24}
                    color={item.selected ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-hint-color)'}
                />
            </Stack>
        </Box>
    );
};

export const OnBoardingCategorySelectionPage: FC = () => {
    const { t } = useTranslation();
    const { recordEvent } = useAnalytics();
    const location = useLocation();
    const templates = ((location.state as { templates?: ListTemplateCategory[] })?.templates ?? []).filter(template => !template.system);
    const [selectableTemplates, setSelectableTemplates] = useState<SelectableTemplate[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [finalizeRegistration] = useMutation<FinalizeRegistrationMutation>(finalizeRegistrationMutation);
    const [createCategory] = useMutation<CreateCategoryMutation, CreateCategoryMutationVariables>(createCategoryMutation);

    setTemplateTranslations(templates);

    useEffect(() => {
        if (templates.length > 0 && selectableTemplates.length === 0) {
            setSelectableTemplates(templates.map(template => ({ template, selected: false })));
        }
    }, [templates, selectableTemplates.length]);

    const handleContinue = useCallback(async () => {
        const selectedTemplates = selectableTemplates.filter(item => item.selected).map(item => item.template);

        setIsCreating(true);
        try {
            await Promise.all(
                selectedTemplates.map(template =>
                    createCategory({
                        variables: {
                            name: template.name,
                            icon: template.icon,
                            iconColor: template.iconColor,
                            parentId: null,
                            goalEnabled: !!template.goal,
                            templateId: template.id,
                        },
                        update: () => {},
                    }),
                ),
            );

            const res = await finalizeRegistration();
            await client.resetStore();

            const currentData = loginData();
            if (currentData && res.data?.updateUser?.registrationStage) {
                loginData({ ...currentData, registrationStage: res.data.updateUser.registrationStage });
            }
            recordEvent(AnalyticsEventType.ONBOARDING_COMPLETED, {
                selected_category_count: selectedTemplates.length,
            });
        } catch (error) {
            console.error('Failed to create categories:', error);
        } finally {
            setIsCreating(false);
            hapticFeedback.impactOccurred.ifAvailable('medium');
        }
    }, [selectableTemplates, createCategory, finalizeRegistration, recordEvent]);

    const handleTemplateToggle = useCallback((index: number) => {
        setSelectableTemplates(previous =>
            previous.map((item, itemIndex) => (itemIndex === index ? { ...item, selected: !item.selected } : item)),
        );
    }, []);
    const selectedCount = selectableTemplates.filter(item => item.selected).length;

    return (
        <Page
            back={false}
            insetEnabled={false}
            edgeToEdge
            footer={
                <ButtonDock
                    main={{
                        text: selectedCount > 0 ? t('onboarding.categorySelection.startJourney') : t('onboarding.categorySelection.skip'),
                        onClick: handleContinue,
                        loading: isCreating,
                    }}
                />
            }
        >
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    overflow: 'hidden',
                    bgcolor: 'var(--tg-theme-secondary-bg-color)',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        paddingTop:
                            'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px) + 12px)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingBottom: miniButtonDockScrollBottomPad,
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack alignItems="center" spacing={0.75} sx={{ px: 1, pb: 1 }}>
                            <Text weight="2" style={{ fontSize: 24, lineHeight: 1.15, textAlign: 'center' }}>
                                {t('onboarding.categorySelection.title')}
                            </Text>
                            <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 14, textAlign: 'center' }}>
                                {t('onboarding.categorySelection.description')}
                            </Caption>
                        </Stack>
                        <LiquidGlassRowCard borderRadius={16}>
                            {selectableTemplates.map((item, index) => (
                                <SelectableTemplateRow
                                    key={item.template.id}
                                    item={item}
                                    showDivider={index > 0}
                                    onToggle={() => handleTemplateToggle(index)}
                                />
                            ))}
                        </LiquidGlassRowCard>
                    </Stack>
                </Box>
            </Box>
        </Page>
    );
};
